import { useEffect, useState } from "react";
import WebContentItem from "./WebContentItem";
import {
  ArticleActions,
  Item,
  ItemWithMetaData,
  ItemsForReviewResponse,
} from "../api/Items";
import { ReportSummaryItemApi } from "../api/Reports";
import PaginationInMemory from "../shared-components/PaginationInMemory";
import ButtonWebcontentAddNew from "./ButtonWebContentAddNew";
import { useApplicationToast } from "./ApplicationToastProvider";
import AddItemIcon from "../shared-components/icons/AddItem";
import {
  ArrowPathIcon,
  TrashIcon,
  ExclamationCircleIcon,
  CogIcon,
} from "@heroicons/react/24/outline";
import ButtonWebContentAddProfile from "./ButtonWebContentAddProfile";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import { useUpdateManyArticleRating } from "../api/ItemsApi";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import DropdownComboSimple, {
  DropdownItem,
} from "../shared-components/DropdownComboSimple";
import ButtonManualScrapeWebContent from "./ButtonManualScrapeWebContent";
import { useReportTimer } from "../reports/ReportDetailProvider";
import { useWebContentActions } from "./useWebContentActions";

type Props = {
  report: ReportSummaryItemApi;
  articleAction: ArticleActions;
  time?: Date;
  behaviors?: string[];
  isReadOnly?: boolean;
  activateDoneBtn?: (value: boolean) => void;
  handleExit: () => void;
  articles: ItemsForReviewResponse;
  handleNextPage: (
    articleAction: ArticleActions,
    page: number,
    keep?: boolean,
    newPageSize?: number
  ) => void;
  handleAppend: (
    articleAction: ArticleActions,
    reviewArticle: ItemWithMetaData[]
  ) => void;
  isLoadingArticles?: boolean;
  pageSizeReviewOptions?: DropdownItem[];
  pageSizeSelection?: DropdownItem;
  isAmendment: boolean;
  webContentParent?: Item;
  didScrapeFail?: boolean;
  profileUUID?: string;
};

const WebContentReview = ({
  report,
  articleAction,
  time,
  isReadOnly,
  behaviors,
  activateDoneBtn,
  handleExit,
  articles,
  handleNextPage,
  handleAppend,
  isLoadingArticles,
  pageSizeReviewOptions,
  pageSizeSelection,
  isAmendment,
  webContentParent,
  didScrapeFail,
  profileUUID,
}: Props) => {
  const { showLargeToast } = useApplicationToast();
  const pageSize = 25;
  const [page, setPage] = useState(1);
  const [ratedArticles, setRatedArticles] = useState<string[]>([]);
  const [completedArticles, setCompletedArticles] = useState<string[]>([]);
  const [selectedArticles, setSelectedArticles] = useState<ItemWithMetaData[]>(
    []
  );
  const [configurePageSize, setConfigurePageSize] = useState(false);
  const { resetTimer } = useReportTimer();
  const { refreshArticlesByAction } = useWebContentActions(report.report_uuid);

  const {
    mutateAsync: updateManyArticleRating,
    isLoading: isLoadingUpdateManyArticleRating,
    error: errorUpdateManyArticleRating,
  } = useUpdateManyArticleRating();

  useEffect(() => {
    const total: number = articles?.total ?? 0;
    if (articleAction === ArticleActions.REVIEW && activateDoneBtn) {
      const allArticlesRated =
        articles.items.length === 0 && ratedArticles.length === total;
      const noMoreArticlesInPage = total === 0;
      activateDoneBtn(allArticlesRated || noMoreArticlesInPage);
    }
  }, [articles, articleAction, ratedArticles, pageSize, activateDoneBtn]);

  function title() {
    switch (articleAction) {
      case ArticleActions.REVIEW:
        return "Review Discovered Web Content";
      case ArticleActions.ADDED:
        return "Added Web Content";
      case ArticleActions.REMOVED:
        return "Removed Web Content";
    }
  }

  function changePage(newPage: number) {
    setPage(newPage);
  }

  function handleSelection(item: ItemWithMetaData, value: boolean) {
    let newSelection = [...selectedArticles];
    if (value) {
      newSelection.push(item);
    } else {
      newSelection = selectedArticles.filter(
        (p) => p.item.external_id !== item.item.external_id
      );
    }
    setSelectedArticles(newSelection);
  }

  function onAction(
    action: "edit" | "remove" | "add" | "cancel" | "completed" | "selected",
    values: ItemWithMetaData
  ) {
    // TODO: fix the webcontent item as it's being removed before getting the action
    // for that we will need to change the way the mutate operation happen so is no longer inside the webcontent item
    // rather it will be done in here, when action is `add` or `remove` we will call the mutate operation
    if (action === "selected") return;
    if (action === "completed") {
      setCompletedArticles([...completedArticles, values.item.external_id]);
      return;
    }
    if (action === "add" || action === "remove") {
      const article = values as ItemWithMetaData;
      if (!article) return;
      setRatedArticles((prev) => [...prev, article.item.external_id]);
    }

    if (action === "add") {
      handleAppend(ArticleActions.ADDED, [values]);
    }

    if (action === "remove") {
      handleAppend(ArticleActions.REMOVED, [values]);
    }

    setSelectedArticles((prev) => {
      return prev.filter((p) => p.item.external_id !== values.item.external_id);
    });

    switch (action) {
      case "remove":
        showLargeToast?.({
          title: "Removed!",
          message: "The content item has been removed.",
          icon: (
            <div className="bg-red-100 text-red-600 rounded-full p-2">
              <TrashIcon className="w-6" />
            </div>
          ),
        });
        break;
      case "add":
        showLargeToast?.({
          title: "Added!",
          message:
            "The content item has been successfully added to the report. ",
          icon: (
            <div className="bg-green-100 text-green-600 rounded-full p-2">
              <AddItemIcon className="w-6" />
            </div>
          ),
        });
        break;
    }
  }

  function handleManyActions(
    articles: ItemWithMetaData[],
    action: ArticleActions
  ) {
    const articlesRatings = articles.map((article) => ({
      item_uuid: article.item.external_id,
      article_action: action,
    }));
    updateManyArticleRating(articlesRatings).then(() => {
      handleAppend(ArticleActions.REMOVED, articles);
      setSelectedArticles([]);
      const manyRatedIds = articles.map((article) => article.item.external_id);
      setRatedArticles((prev) => [...prev, ...manyRatedIds]);
      showLargeToast?.({
        title: "Removed!",
        message: `${articles.length} Articles have been removed.`,
        icon: (
          <div className="bg-red-100 text-red-600 rounded-full p-2">
            <TrashIcon className="w-6" />
          </div>
        ),
      });
    });
  }

  useEffect(() => {
    if (
      articleAction === ArticleActions.REVIEW &&
      articles?.total > 0 &&
      +(pageSizeSelection?.id ?? pageSize) === ratedArticles.length
    ) {
      setRatedArticles([]);
      setCompletedArticles([]);
      handleNextPage(articleAction, 1);
    }
    if (
      (articles?.items ?? []).length > 0 &&
      articles?.items?.length === ratedArticles.length &&
      ratedArticles.length === completedArticles.length
    ) {
      setRatedArticles([]);
      setCompletedArticles([]);
      handleNextPage(articleAction, page);
    }
  }, [
    articles?.items,
    articles?.items?.length,
    completedArticles.length,
    handleNextPage,
    page,
    articleAction,
    ratedArticles,
    articles?.total,
    pageSizeSelection?.id,
  ]);

  const totalCount = articles?.total ?? 1;

  function hideItem(item: ItemWithMetaData) {
    return ratedArticles.some((rated) => rated === item.item.external_id);
  }

  const showLoading =
    !!isLoadingArticles ||
    (articles?.items?.length === ratedArticles.length &&
      ratedArticles.length < completedArticles.length);

  if (showLoading)
    return (
      <div className="border border-gray-400 rounded-md p-4 my-4">
        <div className="w-full skeleton__grey skeleton-heading"></div>
        <div className="border-b border-gray-400 mb-3 pb-3"></div>
        <div className="w-full skeleton__grey skeleton-small"></div>
      </div>
    );

  if (
    !showLoading &&
    articleAction === ArticleActions.REVIEW &&
    articles.items.length === 0
  ) {
    return (
      <div className="text-xl text-center py-6">
        <div className="text-xl font-bold text-black">
          No Web Content - Automated Scraping Unsuccessful
        </div>
        <div className="text-sm text-gray-600">
          Please use Manual Web Search Extension to add web content
        </div>
        <ButtonManualScrapeWebContent
          report={report}
          webContentParent={webContentParent}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex mt-3 items-center gap-2">
        <span className="text-2xl font-extrabold leading-8 tracking-normal truncate">
          {title()}
        </span>
        <span className="text-xs font-medium text-orange-500 cursor-pointer ml-auto"></span>
        {!isReadOnly && behaviors && profileUUID && (
          <ButtonWebcontentAddNew
            handleAction={() => {
              refreshArticlesByAction(ArticleActions.ADDED, 1);
              showLargeToast?.({
                title: "Added!",
                message:
                  "The content item has been successfully added to the report. ",
                icon: (
                  <div className="bg-green-100 text-green-600 rounded-full p-2">
                    <AddItemIcon className="w-6" />
                  </div>
                ),
              });
              resetTimer?.();
            }}
            behaviors={behaviors}
            report={report}
            profileUUID={profileUUID}
          />
        )}
        <ButtonWebContentAddProfile
          report={report}
        ></ButtonWebContentAddProfile>
        {articleAction === ArticleActions.REVIEW && (
          <CogIcon
            className="w-6 cursor-pointer text-gray-600"
            onClick={() => setConfigurePageSize((prev) => !prev)}
          />
        )}
      </div>
      {articles.total > 0 && ratedArticles.length < articles.total ? (
        <>
          <div>
            {pageSizeReviewOptions && (
              <div className="flex justify-end pt-2 items-center gap-2 text-sm">
                {configurePageSize && (
                  <>
                    <div>Articles to load:</div>
                    <DropdownComboSimple
                      items={pageSizeReviewOptions}
                      containerClassName="w-36"
                      name="pageSize"
                      initial={pageSizeSelection}
                      onChange={(selection) => {
                        handleNextPage(
                          articleAction,
                          page,
                          false,
                          +selection.id
                        );
                        setSelectedArticles([]);
                      }}
                    ></DropdownComboSimple>
                  </>
                )}
              </div>
            )}
          </div>
          <div>
            {articles?.items?.map((article) => (
              <WebContentItem
                isReadOnly={isReadOnly}
                isAmendment={isAmendment}
                item={article}
                key={article?.item?.external_id}
                handleAction={(action, value) => {
                  if (action === "selected" && typeof value === "boolean") {
                    handleSelection(article, value);
                  }

                  onAction(action, article);

                  if (action === "edit" && value) {
                    document.getElementById("btn_wc_add_" + value)?.click();
                  }
                }}
                articleAction={articleAction}
                behaviors={behaviors}
                hide={hideItem(article)}
              />
            ))}
          </div>
          <div>
            {articleAction === ArticleActions.REVIEW &&
              articles.items.length === 0 && (
                <Button
                  text={
                    <div className="flex gap-3 items-center px-2 py-0.5 text-gray-500">
                      <ArrowPathIcon className="w-5" />
                      <span className="text-gray-700">Load More</span>
                    </div>
                  }
                  colorType={ButtonColorTypes.cancel}
                  onClick={() => {
                    // this action works as load more
                    handleNextPage(articleAction, 1, true);
                    setRatedArticles([]);
                  }}
                />
              )}
            {articleAction !== ArticleActions.REVIEW && (
              <PaginationInMemory
                currentPage={page}
                pageSize={pageSize}
                totalCount={totalCount}
                pageChange={(newPageIdx) => {
                  changePage(newPageIdx);
                  handleNextPage(articleAction, newPageIdx);
                }}
              />
            )}
          </div>
          {selectedArticles?.length > 0 && (
            <div className="fixed bottom-0 left-72 cursor-pointer">
              <div
                className="bg-red-500 rounded-t-md flex gap-3 items-center px-2 py-1 text-red-100 font-light"
                onClick={() => {
                  handleManyActions?.(selectedArticles, ArticleActions.REMOVED);
                }}
              >
                {isLoadingUpdateManyArticleRating && <LoadingSpinner />}
                {!isLoadingUpdateManyArticleRating && (
                  <>
                    <TrashIcon className="w-5" />
                    <span>
                      Remove <b>{selectedArticles?.length}</b> articles
                    </span>
                    {errorUpdateManyArticleRating && (
                      <div title="Error Removing articles">
                        <ExclamationCircleIcon className="w-6" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-xl text-center py-6">No Web Content Found</div>
      )}
    </div>
  );
};

export default WebContentReview;
